import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import apiServices from '../api/migratedJuntoAPI.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const [accountData, setAccountData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authReadyResolve, setAuthReadyResolve] = useState(null);

  // Create auth ready promise on mount
  useEffect(() => {
    const promise = new Promise((resolve) => {
      setAuthReadyResolve(() => resolve);
    });
    apiServices.referenceDataService.setAuthReady(promise);
  }, []);

  // Register the token getter function with the API client only when signed in
  useEffect(() => {
    // Only update token getter when Clerk is fully loaded
    if (!isLoaded) {
      return;
    }
    
    if (isSignedIn && getToken) {
      apiServices.setGetTokenFunction(getToken);
    } else {
      // Only clear when we're sure the user is not signed in
      apiServices.setGetTokenFunction(null);
    }
    
    // Mark auth as ready so API calls can proceed
    apiServices.markAuthReady();
  }, [isSignedIn, getToken, isLoaded]);

  // Update API client with Clerk token
  useEffect(() => {
    const updateApiToken = async () => {
      if (isSignedIn && getToken) {
        try {
          const token = await getToken();
          if (token) {
            // Update the API client to use Clerk token
            apiServices.setClerkToken(token);
            // Resolve auth ready promise
            if (authReadyResolve) {
              authReadyResolve();
            }
          }
        } catch (error) {
          console.error('Failed to get Clerk token:', error);
          // Resolve anyway so reference data can load
          if (authReadyResolve) {
            authReadyResolve();
          }
        }
      } else {
        // Clear Clerk token when not signed in
        apiServices.setClerkToken(null);
        // Resolve auth ready promise for non-authenticated state
        if (authReadyResolve) {
          authReadyResolve();
        }
      }
    };

    if (isLoaded) {
      updateApiToken();
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, [isLoaded, isSignedIn, getToken, authReadyResolve]);

  // Also update token on every getToken change (handles token refresh)
  useEffect(() => {
    if (!isSignedIn || !getToken) return;

    // Set up an interval to refresh the token periodically
    const refreshInterval = setInterval(async () => {
      try {
        const token = await getToken({ skipCache: true });
        if (token) {
          apiServices.setClerkToken(token);
        }
      } catch (error) {
        console.error('Failed to refresh Clerk token:', error);
      }
    }, 5 * 60 * 1000); // Refresh every 5 minutes

    return () => clearInterval(refreshInterval);
  }, [isSignedIn, getToken]);

  // Load account data when user signs in
  useEffect(() => {
    const loadAccountData = async () => {
      if (!isSignedIn || !clerkUser) {
        setAccountData(null);
        return;
      }

      try {
        // Get user data from our backend using Clerk user ID
        const backendUser = await apiServices.userService.getUserByClerkId(clerkUser.id);
        
        if (backendUser && backendUser.id) {
          // Load account data in parallel
          const [wallet, memberships, bookings, purchaseHistory] = await Promise.all([
            apiServices.userService.getUserWallet(backendUser.id).catch(() => null),
            apiServices.userService.getUserMemberships(backendUser.id).catch(() => null),
            apiServices.userService.getUserBookings(backendUser.id).catch(() => []),
            apiServices.userService.getPurchaseHistory().catch(() => [])
          ]);

          setAccountData({
            wallet,
            memberships,
            upcomingClasses: bookings?.filter(b => 
              new Date(b.class?.start_time) > new Date()
            ).slice(0, 10) || [],
            classHistory: bookings?.filter(b => 
              new Date(b.class?.start_time) <= new Date()
            ).slice(0, 20) || [],
            purchaseHistory: purchaseHistory || []
          });
        }
      } catch (error) {
        console.warn('Failed to load account data:', error);
        setAccountData(null);
      }
    };

    if (isLoaded) {
      loadAccountData();
    }
  }, [isLoaded, isSignedIn, clerkUser]);

  // Refresh account data (useful after purchases, bookings, etc.)
  const refreshAccountData = async () => {
    if (!isSignedIn || !clerkUser) return { success: false, error: 'Not authenticated' };

    try {
      const backendUser = await apiServices.userService.getUserByClerkId(clerkUser.id);
      
      if (backendUser && backendUser.id) {
        const [wallet, memberships, bookings, purchaseHistory] = await Promise.all([
          apiServices.userService.getUserWallet(backendUser.id).catch(() => null),
          apiServices.userService.getUserMemberships(backendUser.id).catch(() => null),
          apiServices.userService.getUserBookings(backendUser.id).catch(() => []),
          apiServices.userService.getPurchaseHistory().catch(() => [])
        ]);

        setAccountData({
          wallet,
          memberships,
          upcomingClasses: bookings?.filter(b => 
            new Date(b.class?.start_time) > new Date()
          ).slice(0, 10) || [],
          classHistory: bookings?.filter(b => 
            new Date(b.class?.start_time) <= new Date()
          ).slice(0, 20) || [],
          purchaseHistory: purchaseHistory || []
        });

        return { success: true };
      }
      
      return { success: false, error: 'User not found' };
    } catch (error) {
      console.error('Failed to refresh account data:', error);
      return { success: false, error: error.message };
    }
  };

  // Map Clerk user to our user format
  const user = clerkUser ? {
    id: clerkUser.id,
    email: clerkUser.primaryEmailAddress?.emailAddress,
    firstName: clerkUser.firstName,
    lastName: clerkUser.lastName,
    fullName: clerkUser.fullName,
    imageUrl: clerkUser.imageUrl,
    clerkId: clerkUser.id,
    phone: clerkUser.primaryPhoneNumber?.phoneNumber,
    instagram: clerkUser.unsafeMetadata?.instagram,
    createdAt: clerkUser.createdAt,
  } : null;

  const value = {
    user,
    accountData,
    isLoading: !isLoaded || isLoading,
    isInitialized: isLoaded && isInitialized,
    isAuthenticated: isSignedIn,
    refreshAccountData,
    // These methods are handled by Clerk's UI components
    login: async () => {
      console.warn('Login is now handled by Clerk UI components');
      return { success: false, error: 'Use Clerk UI components for login' };
    },
    signup: async () => {
      console.warn('Signup is now handled by Clerk UI components');
      return { success: false, error: 'Use Clerk UI components for signup' };
    },
    logout: async () => {
      console.warn('Logout is now handled by Clerk UI components');
      return { success: false, error: 'Use Clerk UI components for logout' };
    },
    updateProfile: async () => {
      console.warn('Profile update is now handled by Clerk UI components');
      return { success: false, error: 'Use Clerk UI components for profile updates' };
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};